<template>
    <RegularModal ref="modal">
        <template v-slot:title>{{ translate("admin_bracket_listing.number_of_medals") }}</template>
        <template v-slot:body>
            <div class="columns">
                <div class="column">
                    <Notification class="is-info has-text-centered">
                        <span class="icon">
                            <i class="fas fa-question-circle"></i>
                        </span>
                        <span>{{ translate("admin_bracket_listing.medals_calculation_info") }}</span>
                    </Notification>
                </div>
            </div>
            <div class="columns">
                <div class="column">
                    <div class="tabs is-fullwidth">
                        <ul>
                            <li :class="{ 'is-active': view === MedalCalculationStrategy.AllRegistrations }">
                                <a @click="switchView(MedalCalculationStrategy.AllRegistrations)">
                                    {{ translate("admin_bracket_listing.all_registrations") }}
                                </a>
                            </li>
                            <li :class="{ 'is-active': view === MedalCalculationStrategy.BracketsConfiguration }">
                                <a @click="switchView(MedalCalculationStrategy.BracketsConfiguration)">
                                    {{ translate("admin_bracket_listing.only_confirmed_payments") }}
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <div class="columns is-centered">
                <LoadingSpinner2 class="has-added-margin" v-if="inProgress" />

                <div class="column" v-else>
                    <MedalStat class="mb-3 is-gold" :value="gold" />
                    <MedalStat class="mb-3 is-silver" :value="silver" />
                    <MedalStat class="is-bronze" :value="bronze" />

                    <div v-if="view === MedalCalculationStrategy.BracketsConfiguration" class="has-text-centered">
                        {{ translate("admin_bracket_listing.bracket_configuration_explanation") }}
                    </div>
                </div>
            </div>
        </template>
        <template v-slot:footer>
            <button class="button" @click="close">
                {{ translate("common.close") }}
            </button>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref } from "vue";
    import RegularModal from "../../Common/RegularModal.vue";
    import MedalStat from "./MedalStat.vue";
    import LoadingSpinner2 from "../../Common/LoadingSpinner2.vue";
    import Notification from "../../Common/Notification.vue";
    import { translate } from "../../Common/translations";
    import { handleError } from "../../Common/errorHandler";
    import bracketsApi, { MedalCalculationStrategy, MedalCalculationStrategyType } from "../../Api/brackets";
    import { useModal } from "../../Common/useModal";

    const props = defineProps(["eventId"]);

    const view = ref<MedalCalculationStrategyType>(MedalCalculationStrategy.AllRegistrations);
    const gold = ref(0);
    const silver = ref(0);
    const bronze = ref(0);
    const inProgress = ref(false);

    const { modal, open: openModal, close } = useModal();

    function open() {
        openModal();
        calculateMedalPlaces();
    }

    function switchView(newView: MedalCalculationStrategyType) {
        view.value = newView;
        calculateMedalPlaces();
    }

    function calculateMedalPlaces() {
        inProgress.value = true;

        return bracketsApi
            .calculateMedalPlaces(props.eventId, view.value)
            .then(medals => {
                gold.value = medals["1"];
                silver.value = medals["2"];
                bronze.value = medals["3"];
            })
            .catch(handleError)
            .finally(() => {
                inProgress.value = false;
            });
    }

    defineExpose({ open });
</script>

<style scoped>
    .is-gold {
        color: gold;
    }

    .is-silver {
        color: silver;
    }

    .is-bronze {
        color: saddlebrown;
    }
</style>
